import React, { useState } from 'react';
import { Building2, Phone, MapPin, Menu, X } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './ui/Button';
import { AGENCY_INFO, LOGO_URL } from '../constants';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Helper to check active state
    const isActive = (path) => currentPath === path ? 'text-[#EA723D]' : '';

    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-[100] shadow-sm font-sans">
            {/* Top Bar Contact */}
            <div className="bg-[#2C353D] text-white py-2 px-4">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-xs font-medium gap-2">
                    <div className="flex gap-6">
                        <span className="flex items-center gap-2"><Building2 size={12} className="text-[#EA723D]" /> {AGENCY_INFO.name}</span>
                        <span className="hidden md:flex items-center gap-2 text-white/80">{AGENCY_INFO.tagline}</span>
                    </div>
                    <div className="flex gap-6">
                        <span className="flex items-center gap-2"><Phone size={12} className="text-[#EA723D]" /> {AGENCY_INFO.phone}</span>
                        <span className="hidden md:flex items-center gap-2"><MapPin size={12} className="text-[#EA723D]" /> {AGENCY_INFO.sector}</span>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-7xl mx-auto px-4 h-24 flex items-center justify-between">
                {/* Left Side: Desktop Menu / Mobile Placeholder */}
                <div className="flex-1 flex items-center">
                    <div className="hidden min-[1020px]:flex items-center gap-6 text-sm font-semibold text-gray-500 uppercase tracking-wide">
                        <Link to="/" onClick={() => window.scrollTo(0, 0)} className={`hover:text-[#EA723D] transition-colors ${isActive('/')}`}>Biens</Link>
                        <Link to="/histoire" onClick={() => window.scrollTo(0, 0)} className={`hover:text-[#EA723D] transition-colors ${isActive('/histoire')}`}>Notre Histoire</Link>
                        <Link to="/parcours" onClick={() => window.scrollTo(0, 0)} className={`hover:text-[#EA723D] transition-colors ${isActive('/parcours')}`}>L'Accompagnement</Link>
                    </div>
                </div>

                {/* Center Side: Logo */}
                <div
                    className="flex-shrink-0 flex items-center justify-center cursor-pointer h-full max-w-[65%] md:max-w-none"
                    onClick={() => { window.location.href = '/'; }}
                >
                    <img
                        src={LOGO_URL}
                        alt={AGENCY_INFO.name}
                        className="h-14 md:h-20 w-auto object-contain translate-y-0.5 md:translate-y-1"
                        style={{ mixBlendMode: 'multiply' }}
                    />
                </div>

                {/* Right Side: Desktop CTA / Mobile Hamburger */}
                <div className="flex-1 flex justify-end items-center">
                    <div className="hidden min-[1020px]:flex items-center gap-8">
                        <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className={`text-sm font-semibold text-gray-500 uppercase tracking-wide hover:text-[#EA723D] transition-colors ${isActive('/contact')}`}>Contact</Link>
                        <Button variant="primary" onClick={() => { navigate('/estimation'); window.scrollTo(0, 0); }} className="py-2.5 px-6 text-xs shadow-lg hover:shadow-[#EA723D]/20 transition-all font-bold">
                            Estimer mon bien
                        </Button>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="min-[1020px]:hidden text-[#2C353D] cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition-colors ml-2"
                        onClick={() => setIsMenuOpen(true)}
                        aria-label="Ouvrir le menu de navigation"
                        aria-expanded={isMenuOpen}
                    >
                        <Menu size={28} />
                    </button>
                </div>
            </div>

            {/* Mobile Menu Drawer */}
            <AnimatePresence>
                {isMenuOpen && (
                    <>
                        {/* Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMenuOpen(false)}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
                        />
                        {/* Drawer */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: "spring", damping: 25, stiffness: 200 }}
                            className="fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl z-[101] p-8 flex flex-col gap-8"
                        >
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-xl font-semibold text-[#2C353D] uppercase tracking-wider">Menu</span>
                                <button
                                    onClick={() => setIsMenuOpen(false)}
                                    className="p-2 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors text-[#2C353D]"
                                    aria-label="Fermer le menu de navigation"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <nav className="flex flex-col gap-6 text-xl font-semibold text-[#2C353D] uppercase tracking-wide">
                                <Link to="/" onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }} className="hover:text-[#EA723D] transition-colors flex items-center gap-4 border-b border-gray-50 pb-4">
                                    Biens
                                </Link>
                                <Link to="/histoire" onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }} className="hover:text-[#EA723D] transition-colors flex items-center gap-4 border-b border-gray-50 pb-4">
                                    Notre Histoire
                                </Link>
                                <Link to="/parcours" onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }} className="hover:text-[#EA723D] transition-colors flex items-center gap-4 border-b border-gray-50 pb-4">
                                    L'Accompagnement
                                </Link>
                                <Link to="/contact" onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }} className="hover:text-[#EA723D] transition-colors flex items-center gap-4 border-b border-gray-50 pb-4">
                                    Contact
                                </Link>
                            </nav>

                            <div className="mt-auto space-y-6">
                                <Button variant="primary" onClick={() => { navigate('/estimation'); setIsMenuOpen(false); window.scrollTo(0, 0); }} className="w-full justify-center py-4 text-base shadow-lg animate-pulse">
                                    Estimer mon bien
                                </Button>

                                <div className="text-center space-y-2 pt-6 border-t border-gray-100">
                                    <p className="text-xs text-gray-400 font-medium">CONTACT DIRECT</p>
                                    <p className="text-lg font-semibold text-[#2C353D]">{AGENCY_INFO.phone}</p>
                                    <p className="text-sm text-gray-500">{AGENCY_INFO.email}</p>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
